import i18next from 'i18next';
import bsBetting from 'locales/bs/betting.json';
import deBetting from 'locales/de/betting.json';
import frBetting from 'locales/fr/betting.json';
import hrBetting from 'locales/hr/betting.json';
import itBetting from 'locales/it/betting.json';
import sqBetting from 'locales/sq/betting.json';
import srBetting from 'locales/sr/betting.json';
import trBetting from 'locales/tr/betting.json';
import esBetting from 'locales/es/betting.json';
import { appLanguageOf, type TranslatedLanguage } from 'config/languages';

/**
 * Translations for the provider's English market and selection names.
 *
 * The feed names markets in English ("1st Half Asian Handicap", "Map 2 -
 * Totals", "Set 1 - Game 8 Winner") and only a handful have a catalogue id in
 * betMarkets, so most of the offer read in English. Lookup goes:
 *   1. the phrase dictionary in locales/<lang>/betting.json (exact phrase,
 *      ignoring case, spacing and punctuation);
 *   2. composition rules that peel a known qualifier off the front or back
 *      ("1st Half ...", "Map 2 - ...", "Set 1 - Game 8 ...", "Alternative
 *      ...", "... 3-Way", "... O/U", "... (2-1)") and translate the rest
 *      recursively;
 *   3. templates for names that embed a player, a team or a number.
 * Anything still unknown returns null so the caller keeps the English name,
 * rather than showing half a translation.
 */

type Lang = TranslatedLanguage;

type BettingFile = {
  markets: Record<string, string>;
  selections: Record<string, string>;
};

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

const indexed = (entries: Record<string, string>) =>
  new Map(Object.entries(entries).map(([en, tr]) => [normalize(en), tr]));

const FILES: Record<Lang, BettingFile> = {
  de: deBetting,
  sr: srBetting,
  hr: hrBetting,
  bs: bsBetting,
  it: itBetting,
  fr: frBetting,
  sq: sqBetting,
  tr: trBetting,
  es: esBetting,
};

const DICTIONARIES = Object.fromEntries(
  Object.entries(FILES).map(([lang, file]) => [
    lang,
    {
      markets: indexed(file.markets),
      selections: indexed(file.selections),
    },
  ])
) as Record<
  Lang,
  { markets: Map<string, string>; selections: Map<string, string> }
>;

const ORDINALS: Record<string, number> = {
  '1st': 1,
  first: 1,
  '2nd': 2,
  second: 2,
  '3rd': 3,
  third: 3,
  '4th': 4,
  fourth: 4,
};

const ordinal = (word: string) => {
  const w = word.toLowerCase();
  if (ORDINALS[w]) return ORDINALS[w];
  const m = w.match(/^(\d+)(st|nd|rd|th)$/);
  return m ? Number(m[1]) : null;
};

type Phrasing = {
  half: (base: string, n: number) => string;
  quarter: (base: string, n: number) => string;
  period: (base: string, n: number) => string;
  set: (base: string, n: number) => string;
  game: (base: string, n: number) => string;
  games: (base: string, a: number, b: number) => string;
  map: (base: string, n: number) => string;
  round: (base: string, n: number) => string;
  innings: (base: string, n: number) => string;
  alternative: (base: string) => string;
  way: (base: string, n: number) => string;
  overUnder: (base: string) => string;
  first10: (base: string) => string;
  firstGame: (base: string) => string;
  enhanced: (base: string) => string;
  raceToPoints: (n: string) => string;
  nthGoal: (n: number) => string;
  /** "Time of 4th Goal" */
  timeOfGoal: (n: number) => string;
  /** "4th Goal Method": how the goal is scored. */
  goalMethod: (n: number) => string;
  scoreAfterSets: (n: string) => string;
  scoreAfterPoints: (n: string) => string;
  leadAfterPoints: (n: string) => string;
  pointBetting: (where: string) => string;
  setGameLabel: (set: number, game: number) => string;
  tieBreakLabel: (set: number) => string;
  start: (value: string) => string;
  serviceGame: (player: string, what: string) => string;
  serviceGameParts: Record<string, string>;
};

/** French ordinals: 1er / 1re, then 2e, 3e... */
const frOrd = (n: number, feminine = false) =>
  n === 1 ? (feminine ? '1re' : '1er') : `${n}e`;

/** Albanian ordinal after "të": parë, dytë, then 3-të, 4-t... */
const sqOrd = (n: number) =>
  n === 1 ? 'parë' : n === 2 ? 'dytë' : n === 3 ? '3-të' : `${n}-t`;

/** Spanish masculine ordinal: 1.er, then 2.º, 3.º... */
const esOrd = (n: number) => (n === 1 ? '1.er' : `${n}.º`);

/** How each language attaches a qualifier to a translated base phrase. */
const PHRASING: Record<Lang, Phrasing> = {
  de: {
    half: (b, n) => `${b} ${n}. Halbzeit`,
    quarter: (b, n) => `${b} ${n}. Viertel`,
    period: (b, n) => `${b} ${n}. Drittel`,
    set: (b, n) => `${b} ${n}. Satz`,
    game: (b, n) => `${b} ${n}. Spiel`,
    games: (b, a, c) => `${b} ${a}. und ${c}. Spiel`,
    map: (b, n) => `Map ${n} – ${b}`,
    round: (b, n) => `Runde ${n} – ${b}`,
    innings: (b, n) => `${b} (${n} Innings)`,
    alternative: (b) => `${b} (Alternativ)`,
    way: (b, n) => `${b} (${n}-Weg)`,
    overUnder: (b) => `${b} Über/Unter`,
    first10: (b) => `${b} (erste 10 Minuten)`,
    firstGame: (b) => `${b} 1. Spiel`,
    enhanced: (b) => `${b} – erhöhte Quoten`,
    raceToPoints: (n) => `Wer erreicht zuerst ${n} Punkte`,
    nthGoal: (n) => `${n}. Tor`,
    timeOfGoal: (n) => `Zeit des ${n}. Tores`,
    goalMethod: (n) => `Art des ${n}. Tores`,
    scoreAfterSets: (n) => `Ergebnis nach ${n} Sätzen`,
    scoreAfterPoints: (n) => `Ergebnis nach ${n} Punkten`,
    leadAfterPoints: (n) => `Führung nach ${n} Punkten`,
    pointBetting: (w) => `Punktwette – ${w}`,
    setGameLabel: (s, g) => `${s}. Satz, ${g}. Spiel`,
    tieBreakLabel: (s) => `Tiebreak ${s}. Satz`,
    start: (v) => `Vorsprung ${v}`,
    serviceGame: (p, w) => `Erstes Aufschlagspiel ${p} – ${w}`,
    serviceGameParts: {
      winners: 'Sieger',
      score: 'Ergebnis',
      towinto: 'Gewinnt zu',
      totalpoints: 'Punkte gesamt',
    },
  },
  sr: {
    half: (b, n) => `${b} - ${n}. poluvreme`,
    quarter: (b, n) => `${b} - ${n}. četvrtina`,
    period: (b, n) => `${b} - ${n}. trećina`,
    set: (b, n) => `${b} - ${n}. set`,
    game: (b, n) => `${b} - ${n}. gem`,
    games: (b, a, c) => `${b} - ${a}. i ${c}. gem`,
    map: (b, n) => `Mapa ${n} - ${b}`,
    round: (b, n) => `Runda ${n} - ${b}`,
    innings: (b, n) => `${b} (${n} ininga)`,
    alternative: (b) => `${b} (alternativa)`,
    way: (b, n) => `${b} (${n} ishoda)`,
    overUnder: (b) => `${b} više/manje`,
    first10: (b) => `${b} (prvih 10 minuta)`,
    firstGame: (b) => `${b} - 1. gem`,
    enhanced: (b) => `${b} - povećane kvote`,
    raceToPoints: (n) => `Ko pre do ${n} poena`,
    nthGoal: (n) => `${n}. gol`,
    timeOfGoal: (n) => `Vreme ${n}. gola`,
    goalMethod: (n) => `Način postizanja ${n}. gola`,
    scoreAfterSets: (n) => `Rezultat posle ${n} seta`,
    scoreAfterPoints: (n) => `Rezultat posle ${n} poena`,
    leadAfterPoints: (n) => `Vodstvo posle ${n} poena`,
    pointBetting: (w) => `Poen po poen - ${w}`,
    setGameLabel: (s, g) => `${s}. set, ${g}. gem`,
    tieBreakLabel: (s) => `tie-break ${s}. seta`,
    start: (v) => `Prednost ${v}`,
    serviceGame: (p, w) => `Prvi servis-gem ${p} - ${w}`,
    serviceGameParts: {
      winners: 'pobednik',
      score: 'rezultat',
      towinto: 'osvaja na',
      totalpoints: 'ukupno poena',
    },
  },
  hr: {
    half: (b, n) => `${b} - ${n}. poluvrijeme`,
    quarter: (b, n) => `${b} - ${n}. četvrtina`,
    period: (b, n) => `${b} - ${n}. trećina`,
    set: (b, n) => `${b} - ${n}. set`,
    game: (b, n) => `${b} - ${n}. gem`,
    games: (b, a, c) => `${b} - ${a}. i ${c}. gem`,
    map: (b, n) => `Mapa ${n} - ${b}`,
    round: (b, n) => `Runda ${n} - ${b}`,
    innings: (b, n) => `${b} (${n} ininga)`,
    alternative: (b) => `${b} (alternativa)`,
    way: (b, n) => `${b} (${n} ishoda)`,
    overUnder: (b) => `${b} više/manje`,
    first10: (b) => `${b} (prvih 10 minuta)`,
    firstGame: (b) => `${b} - 1. gem`,
    enhanced: (b) => `${b} - povećani koeficijenti`,
    raceToPoints: (n) => `Tko prije do ${n} poena`,
    nthGoal: (n) => `${n}. gol`,
    timeOfGoal: (n) => `Vrijeme ${n}. gola`,
    goalMethod: (n) => `Način postizanja ${n}. gola`,
    scoreAfterSets: (n) => `Rezultat nakon ${n} seta`,
    scoreAfterPoints: (n) => `Rezultat nakon ${n} poena`,
    leadAfterPoints: (n) => `Vodstvo nakon ${n} poena`,
    pointBetting: (w) => `Poen po poen - ${w}`,
    setGameLabel: (s, g) => `${s}. set, ${g}. gem`,
    tieBreakLabel: (s) => `tie-break ${s}. seta`,
    start: (v) => `Prednost ${v}`,
    serviceGame: (p, w) => `Prvi servis-gem ${p} - ${w}`,
    serviceGameParts: {
      winners: 'pobjednik',
      score: 'rezultat',
      towinto: 'osvaja na',
      totalpoints: 'ukupno poena',
    },
  },
  bs: {
    half: (b, n) => `${b} - ${n}. poluvrijeme`,
    quarter: (b, n) => `${b} - ${n}. četvrtina`,
    period: (b, n) => `${b} - ${n}. trećina`,
    set: (b, n) => `${b} - ${n}. set`,
    game: (b, n) => `${b} - ${n}. gem`,
    games: (b, a, c) => `${b} - ${a}. i ${c}. gem`,
    map: (b, n) => `Mapa ${n} - ${b}`,
    round: (b, n) => `Runda ${n} - ${b}`,
    innings: (b, n) => `${b} (${n} ininga)`,
    alternative: (b) => `${b} (alternativa)`,
    way: (b, n) => `${b} (${n} ishoda)`,
    overUnder: (b) => `${b} više/manje`,
    first10: (b) => `${b} (prvih 10 minuta)`,
    firstGame: (b) => `${b} - 1. gem`,
    enhanced: (b) => `${b} - povećane kvote`,
    raceToPoints: (n) => `Ko prije do ${n} poena`,
    nthGoal: (n) => `${n}. gol`,
    timeOfGoal: (n) => `Vrijeme ${n}. gola`,
    goalMethod: (n) => `Način postizanja ${n}. gola`,
    scoreAfterSets: (n) => `Rezultat poslije ${n} seta`,
    scoreAfterPoints: (n) => `Rezultat poslije ${n} poena`,
    leadAfterPoints: (n) => `Vodstvo poslije ${n} poena`,
    pointBetting: (w) => `Poen po poen - ${w}`,
    setGameLabel: (s, g) => `${s}. set, ${g}. gem`,
    tieBreakLabel: (s) => `tie-break ${s}. seta`,
    start: (v) => `Prednost ${v}`,
    serviceGame: (p, w) => `Prvi servis-gem ${p} - ${w}`,
    serviceGameParts: {
      winners: 'pobjednik',
      score: 'rezultat',
      towinto: 'osvaja na',
      totalpoints: 'ukupno poena',
    },
  },
  it: {
    half: (b, n) => `${b} - ${n}° tempo`,
    quarter: (b, n) => `${b} - ${n}° quarto`,
    period: (b, n) => `${b} - ${n}° periodo`,
    set: (b, n) => `${b} - ${n}° set`,
    game: (b, n) => `${b} - ${n}° game`,
    games: (b, a, c) => `${b} - ${a}° e ${c}° game`,
    map: (b, n) => `Mappa ${n} - ${b}`,
    round: (b, n) => `Round ${n} - ${b}`,
    innings: (b, n) => `${b} (${n} inning)`,
    alternative: (b) => `${b} (alternativo)`,
    way: (b, n) => `${b} (${n} esiti)`,
    overUnder: (b) => `${b} under/over`,
    first10: (b) => `${b} (primi 10 minuti)`,
    firstGame: (b) => `${b} - 1° game`,
    enhanced: (b) => `${b} - quote maggiorate`,
    raceToPoints: (n) => `Chi arriva prima a ${n} punti`,
    nthGoal: (n) => `${n}° gol`,
    timeOfGoal: (n) => `Minuto del ${n}° gol`,
    goalMethod: (n) => `Modalità del ${n}° gol`,
    scoreAfterSets: (n) => `Punteggio dopo ${n} set`,
    scoreAfterPoints: (n) => `Punteggio dopo ${n} punti`,
    leadAfterPoints: (n) => `In vantaggio dopo ${n} punti`,
    pointBetting: (w) => `Punto per punto - ${w}`,
    setGameLabel: (s, g) => `${s}° set, ${g}° game`,
    tieBreakLabel: (s) => `tie-break del ${s}° set`,
    start: (v) => `Vantaggio ${v}`,
    serviceGame: (p, w) => `Primo game al servizio di ${p} - ${w}`,
    serviceGameParts: {
      winners: 'vincente',
      score: 'punteggio',
      towinto: 'vince a',
      totalpoints: 'punti totali',
    },
  },
  fr: {
    half: (b, n) => `${b} - ${frOrd(n, true)} mi-temps`,
    quarter: (b, n) => `${b} - ${frOrd(n)} quart-temps`,
    period: (b, n) => `${b} - ${frOrd(n, true)} période`,
    set: (b, n) => `${b} - ${frOrd(n)} set`,
    game: (b, n) => `${b} - ${frOrd(n)} jeu`,
    games: (b, a, c) => `${b} - jeux ${a} et ${c}`,
    map: (b, n) => `Carte ${n} - ${b}`,
    round: (b, n) => `Round ${n} - ${b}`,
    innings: (b, n) => `${b} (${n} manches)`,
    alternative: (b) => `${b} (alternatif)`,
    way: (b, n) => `${b} (${n} issues)`,
    overUnder: (b) => `${b} plus/moins`,
    first10: (b) => `${b} (10 premières minutes)`,
    firstGame: (b) => `${b} - 1er jeu`,
    enhanced: (b) => `${b} - cotes boostées`,
    raceToPoints: (n) => `Premier à ${n} points`,
    nthGoal: (n) => `${frOrd(n)} but`,
    timeOfGoal: (n) => `Minute du ${frOrd(n)} but`,
    goalMethod: (n) => `Façon de marquer le ${frOrd(n)} but`,
    scoreAfterSets: (n) => `Score après ${n} sets`,
    scoreAfterPoints: (n) => `Score après ${n} points`,
    leadAfterPoints: (n) => `En tête après ${n} points`,
    pointBetting: (w) => `Point par point - ${w}`,
    setGameLabel: (s, g) => `${frOrd(s)} set, ${frOrd(g)} jeu`,
    tieBreakLabel: (s) => `tie-break du ${frOrd(s)} set`,
    start: (v) => `Avance ${v}`,
    serviceGame: (p, w) => `Premier jeu de service de ${p} - ${w}`,
    serviceGameParts: {
      winners: 'vainqueur',
      score: 'score',
      towinto: 'gagne à',
      totalpoints: 'total de points',
    },
  },
  sq: {
    half: (b, n) => `${b} - pjesa ${n === 1 ? 'e parë' : 'e dytë'}`,
    quarter: (b, n) => `${b} - çereku ${n}`,
    period: (b, n) => `${b} - periudha ${n}`,
    set: (b, n) => `${b} - seti ${n}`,
    game: (b, n) => `${b} - game ${n}`,
    games: (b, a, c) => `${b} - game ${a} dhe ${c}`,
    map: (b, n) => `Harta ${n} - ${b}`,
    round: (b, n) => `Raundi ${n} - ${b}`,
    innings: (b, n) => `${b} (${n} inningje)`,
    alternative: (b) => `${b} (alternative)`,
    way: (b, n) => `${b} (${n} mundësi)`,
    overUnder: (b) => `${b} mbi/nën`,
    first10: (b) => `${b} (10 minutat e para)`,
    firstGame: (b) => `${b} - game i parë`,
    enhanced: (b) => `${b} - koeficientë të rritur`,
    raceToPoints: (n) => `Kush arrin i pari në ${n} pikë`,
    nthGoal: (n) => `Goli ${n}`,
    timeOfGoal: (n) => `Koha e golit të ${sqOrd(n)}`,
    goalMethod: (n) => `Mënyra e golit të ${sqOrd(n)}`,
    scoreAfterSets: (n) => `Rezultati pas ${n} seteve`,
    scoreAfterPoints: (n) => `Rezultati pas ${n} pikësh`,
    leadAfterPoints: (n) => `Në avantazh pas ${n} pikësh`,
    pointBetting: (w) => `Pikë pas pike - ${w}`,
    setGameLabel: (s, g) => `seti ${s}, game ${g}`,
    tieBreakLabel: (s) => `tie-break i setit ${s}`,
    start: (v) => `Avantazh ${v}`,
    serviceGame: (p, w) => `Game-i i parë në shërbim i ${p} - ${w}`,
    serviceGameParts: {
      winners: 'fituesi',
      score: 'rezultati',
      towinto: 'fiton me',
      totalpoints: 'pikë gjithsej',
    },
  },
  // Turkish puts the period first: "1. Yarı - Handikap".
  tr: {
    half: (b, n) => `${n}. Yarı - ${b}`,
    quarter: (b, n) => `${n}. Çeyrek - ${b}`,
    period: (b, n) => `${n}. Periyot - ${b}`,
    set: (b, n) => `${n}. Set - ${b}`,
    game: (b, n) => `${n}. Oyun - ${b}`,
    games: (b, a, c) => `${a}. ve ${c}. Oyun - ${b}`,
    map: (b, n) => `${n}. Harita - ${b}`,
    round: (b, n) => `${n}. Raunt - ${b}`,
    innings: (b, n) => `${b} (${n} İnning)`,
    alternative: (b) => `${b} (Alternatif)`,
    way: (b, n) => `${b} (${n} Seçenekli)`,
    overUnder: (b) => `${b} Alt/Üst`,
    first10: (b) => `${b} (İlk 10 Dakika)`,
    firstGame: (b) => `1. Oyun - ${b}`,
    enhanced: (b) => `${b} - Artırılmış Oranlar`,
    raceToPoints: (n) => `${n} Sayıya İlk Ulaşan`,
    nthGoal: (n) => `${n}. Gol`,
    timeOfGoal: (n) => `${n}. Gol Zamanı`,
    goalMethod: (n) => `${n}. Golün Atılış Şekli`,
    scoreAfterSets: (n) => `${n} Set Sonrası Skor`,
    scoreAfterPoints: (n) => `${n} Sayı Sonrası Skor`,
    leadAfterPoints: (n) => `${n} Sayı Sonrası Önde Olan`,
    pointBetting: (w) => `Sayı Bahsi - ${w}`,
    setGameLabel: (s, g) => `${s}. Set, ${g}. Oyun`,
    tieBreakLabel: (s) => `${s}. Set Tie-break`,
    start: (v) => `Avantaj ${v}`,
    serviceGame: (p, w) => `${p} İlk Servis Oyunu - ${w}`,
    serviceGameParts: {
      winners: 'Kazanan',
      score: 'Skor',
      towinto: 'Kazanma Skoru',
      totalpoints: 'Toplam Sayı',
    },
  },
  es: {
    half: (b, n) => `${b} - ${n}.ª parte`,
    quarter: (b, n) => `${b} - ${n}.º cuarto`,
    period: (b, n) => `${b} - ${n}.º periodo`,
    set: (b, n) => `${b} - ${n}.º set`,
    game: (b, n) => `${b} - ${n}.º juego`,
    games: (b, a, c) => `${b} - juegos ${a} y ${c}`,
    map: (b, n) => `Mapa ${n} - ${b}`,
    round: (b, n) => `Ronda ${n} - ${b}`,
    innings: (b, n) => `${b} (${n} entradas)`,
    alternative: (b) => `${b} (alternativo)`,
    way: (b, n) => `${b} (${n} opciones)`,
    overUnder: (b) => `${b} más/menos`,
    first10: (b) => `${b} (primeros 10 minutos)`,
    firstGame: (b) => `${b} - 1.er juego`,
    enhanced: (b) => `${b} - cuotas mejoradas`,
    raceToPoints: (n) => `Primero en llegar a ${n} puntos`,
    nthGoal: (n) => `${n}.º gol`,
    timeOfGoal: (n) => `Minuto del ${esOrd(n)} gol`,
    goalMethod: (n) => `Forma del ${esOrd(n)} gol`,
    scoreAfterSets: (n) => `Resultado tras ${n} sets`,
    scoreAfterPoints: (n) => `Resultado tras ${n} puntos`,
    leadAfterPoints: (n) => `Por delante tras ${n} puntos`,
    pointBetting: (w) => `Punto a punto - ${w}`,
    setGameLabel: (s, g) => `${s}.º set, ${g}.º juego`,
    tieBreakLabel: (s) => `tie-break del ${s}.º set`,
    start: (v) => `Ventaja ${v}`,
    serviceGame: (p, w) => `Primer juego al servicio de ${p} - ${w}`,
    serviceGameParts: {
      winners: 'ganador',
      score: 'resultado',
      towinto: 'gana a',
      totalpoints: 'total de puntos',
    },
  },
};

type Rule = (
  name: string,
  lang: Lang,
  resolve: (inner: string) => string | null
) => string | null;

const withBase = (
  inner: string,
  resolve: (inner: string) => string | null,
  wrap: (base: string) => string
) => {
  const base = resolve(inner);
  return base == null ? null : wrap(base);
};

/** Names the feed builds around a team or player ("Arsenal Exact Goals"). */
const NAMED_SUFFIXES =
  /^(.+?) (exact goals|goals|totals|alternative totals|to score|exact total goals)$/i;

const MARKET_RULES: Rule[] = [
  (name, lang, resolve) => {
    const m = name.match(/^(.+?)\s*[–-]\s*enhanced prices$/i);
    return m ? withBase(m[1], resolve, PHRASING[lang].enhanced) : null;
  },
  (name, lang, resolve) => {
    const m = name.match(/^alternative (.+)$/i);
    return m ? withBase(m[1], resolve, PHRASING[lang].alternative) : null;
  },
  (name, lang, resolve) => {
    const m = name.match(/^(1st|2nd|first|second) half(?: -)? (.+)$/i);
    const n = m && ordinal(m[1]);
    return m && n
      ? withBase(m[2], resolve, (b) => PHRASING[lang].half(b, n))
      : null;
  },
  (name, lang, resolve) => {
    const m = name.match(/^(1st|2nd|3rd|4th) quarter(?: -)? (.+)$/i);
    const n = m && ordinal(m[1]);
    return m && n
      ? withBase(m[2], resolve, (b) => PHRASING[lang].quarter(b, n))
      : null;
  },
  (name, lang, resolve) => {
    const m =
      name.match(/^(1st|2nd|3rd) period(?: -)? (.+)$/i) ??
      name.match(/^period (\d+)(?: -)? (.+)$/i);
    const n = m && (/^\d+$/.test(m[1]) ? Number(m[1]) : ordinal(m[1]));
    return m && n
      ? withBase(m[2], resolve, (b) => PHRASING[lang].period(b, n))
      : null;
  },
  // "Point Betting - Set 1 Game 8", "Point Betting - Set 2 Tie Break",
  // "Set 1 Tie Break Point Betting"
  (name, lang) => {
    const p = PHRASING[lang];
    let m = name.match(/^point betting - set (\d+),? game (\d+)$/i);
    if (m) return p.pointBetting(p.setGameLabel(Number(m[1]), Number(m[2])));
    m =
      name.match(/^point betting - set (\d+) tie ?break$/i) ??
      name.match(/^set (\d+) tie ?break point betting$/i);
    if (m) return p.pointBetting(p.tieBreakLabel(Number(m[1])));
    return null;
  },
  // "Set 1 - Games 7 & 8 - Winner"
  (name, lang, resolve) => {
    const m = name.match(/^set (\d+) - games (\d+) & (\d+) - (.+)$/i);
    if (!m) return null;
    const p = PHRASING[lang];
    return withBase(m[4], resolve, (b) =>
      p.set(p.games(b, Number(m[2]), Number(m[3])), Number(m[1]))
    );
  },
  (name, lang, resolve) => {
    const m =
      name.match(/^set (\d+)(?: -)? (.+)$/i) ??
      name.match(/^(1st|first) set(?: -)? (.+)$/i);
    if (!m) return null;
    const n = /^\d+$/.test(m[1]) ? Number(m[1]) : ordinal(m[1]);
    return n ? withBase(m[2], resolve, (b) => PHRASING[lang].set(b, n)) : null;
  },
  // "Total Games in Set 2"
  (name, lang, resolve) => {
    const m = name.match(/^(.+) in set (\d+)$/i);
    return m
      ? withBase(m[1], resolve, (b) => PHRASING[lang].set(b, Number(m[2])))
      : null;
  },
  (name, lang, resolve) => {
    const m = name.match(/^game (\d+)(?: -)? (.+)$/i);
    return m
      ? withBase(m[2], resolve, (b) => PHRASING[lang].game(b, Number(m[1])))
      : null;
  },
  (name, lang, resolve) => {
    const m = name.match(/^(1st|first) game(?: -)? (.+)$/i);
    return m ? withBase(m[2], resolve, PHRASING[lang].firstGame) : null;
  },
  // "Score After 3 Points", "Tie Break Lead after 4 Points"
  (name, lang) => {
    const m = name.match(/^(?:tie ?break )?(lead|score) after (\d+) points$/i);
    if (!m) return null;
    return /^lead$/i.test(m[1])
      ? PHRASING[lang].leadAfterPoints(m[2])
      : PHRASING[lang].scoreAfterPoints(m[2]);
  },
  (name, lang, resolve) => {
    const m = name.match(/^map (\d+)(?: -)? (.+)$/i);
    return m
      ? withBase(m[2], resolve, (b) => PHRASING[lang].map(b, Number(m[1])))
      : null;
  },
  (name, lang, resolve) => {
    const m = name.match(/^round (\d+)(?: -)? (.+)$/i);
    return m
      ? withBase(m[2], resolve, (b) => PHRASING[lang].round(b, Number(m[1])))
      : null;
  },
  (name, lang, resolve) => {
    const m = name.match(/^(\d+) innings? (.+)$/i);
    return m
      ? withBase(m[2], resolve, (b) => PHRASING[lang].innings(b, Number(m[1])))
      : null;
  },
  (name, lang, resolve) => {
    const m = name.match(/^1st 10 minutes? (.+)$/i);
    return m ? withBase(m[1], resolve, PHRASING[lang].first10) : null;
  },
  (name, lang) => {
    const m = name.match(/^(\d+(?:st|nd|rd|th)) goal$/i);
    const n = m && ordinal(m[1]);
    return n ? PHRASING[lang].nthGoal(n) : null;
  },
  // Live football counts the goals on: "Time of 4th Goal", "2nd Goal Method".
  (name, lang) => {
    const m = name.match(/^time of (\d+(?:st|nd|rd|th)|first) goal$/i);
    const n = m && ordinal(m[1]);
    return n ? PHRASING[lang].timeOfGoal(n) : null;
  },
  (name, lang) => {
    const m = name.match(/^(\d+(?:st|nd|rd|th)|first) goal method$/i);
    const n = m && ordinal(m[1]);
    return n ? PHRASING[lang].goalMethod(n) : null;
  },
  (name, lang) => {
    const m = name.match(/^score after (\d+) sets$/i);
    return m ? PHRASING[lang].scoreAfterSets(m[1]) : null;
  },
  // Ice hockey "10 Minute Correct Score 30:00-39:59": the window says it.
  (name, _lang, resolve) => {
    const m = name.match(/^10 minute (.+) (\d+:\d+\s*-\s*\d+:\d+)$/i);
    return m ? withBase(m[1], resolve, (b) => `${b} (${m[2]})`) : null;
  },
  (name, lang) => {
    const m = name.match(/^start ([+-]?[\d.]+)$/i);
    return m ? PHRASING[lang].start(m[1]) : null;
  },
  // "3-Way Handicap", "2-Way Corners"
  (name, lang, resolve) => {
    const m = name.match(/^(\d+)[- ]way (.+)$/i);
    return m
      ? withBase(m[2], resolve, (b) => PHRASING[lang].way(b, Number(m[1])))
      : null;
  },
  (name, lang, resolve) => {
    const m = name.match(/^(.+?)\s*\(?(\d+)[- ]way\)?$/i);
    return m
      ? withBase(m[1], resolve, (b) => PHRASING[lang].way(b, Number(m[2])))
      : null;
  },
  (name, lang, resolve) => {
    const m = name.match(/^(.+) o\/u$/i);
    return m ? withBase(m[1], resolve, PHRASING[lang].overUnder) : null;
  },
  // A score, a count or a time window after the name: "Asian Handicap
  // (1-0)", "Round Group Betting (3)", "Winning Margin 1-2", "Next 10 Minutes
  // (40:00 - 49:59)"
  (name, _lang, resolve) => {
    const m =
      name.match(/^(.+?) (\([\d:\s.+-]+\))$/) ??
      name.match(/^(.+?) (\d+-\d+)$/);
    return m ? withBase(m[1], resolve, (b) => `${b} ${m[2]}`) : null;
  },
  (name, _lang, resolve) => {
    const m = name.match(/^(.+) (\d)$/);
    return m ? withBase(m[1], resolve, (b) => `${b} ${m[2]}`) : null;
  },
  (name, lang) => {
    const m = name.match(/^race to (\d+) points$/i);
    return m ? PHRASING[lang].raceToPoints(m[1]) : null;
  },
  (name, lang) => {
    const m = name.match(
      /^first (.+) service game - (winners|score|to win to|total points)$/i
    );
    if (!m) return null;
    const what = PHRASING[lang].serviceGameParts[normalize(m[2])];
    return what ? PHRASING[lang].serviceGame(m[1], what) : null;
  },
  // "Time of 1st Goal - Arsenal": a known market, then a team name.
  (name, _lang, resolve) => {
    const m = name.match(/^(.+?) - ([^-]+)$/);
    if (!m) return null;
    const base = resolve(m[1]);
    return base == null ? null : `${base} - ${m[2].trim()}`;
  },
  // "Arsenal Exact Goals", "Jukurit Totals": a team, then a known market.
  (name, _lang, resolve) => {
    const m = name.match(NAMED_SUFFIXES);
    if (!m) return null;
    const base = resolve(m[2]);
    return base == null ? null : `${m[1]} - ${base}`;
  },
  // Tennis "Katie Swan To" (the pick says "Win a Set"): just the player.
  (name) => {
    const m = name.match(/^(.+\S) to$/i);
    return m ? m[1] : null;
  },
];

const currentLang = (): Lang | null => {
  const lng = appLanguageOf(i18next.resolvedLanguage || i18next.language);
  return lng === 'en' ? null : lng;
};

const MAX_DEPTH = 4;

const resolveMarket = (
  name: string,
  lang: Lang,
  depth: number
): string | null => {
  const hit = DICTIONARIES[lang].markets.get(normalize(name));
  if (hit) return hit;
  if (depth >= MAX_DEPTH) return null;
  const resolve = (inner: string) =>
    resolveMarket(inner.trim(), lang, depth + 1);
  for (const rule of MARKET_RULES) {
    const out = rule(name, lang, resolve);
    if (out != null) return out;
  }
  return null;
};

/**
 * Name of a provider market in the player's language, or null when there is
 * no translation (or the player is on English).
 */
export function translateMarketNameByText(
  name: string | null | undefined
): string | null {
  const raw = String(name ?? '')
    .trim()
    .replace(/\s+/g, ' ');
  const lang = currentLang();
  if (!raw || !lang) return null;
  return resolveMarket(raw, lang, 0);
}

// [singular, plural, Slavic 2-4 form]
type UnitForms = [string, string, string];

const SLAVIC_UNITS: Record<string, UnitForms> = {
  goal: ['gol', 'golova', 'gola'],
  corner: ['korner', 'kornera', 'kornera'],
  card: ['karton', 'kartona', 'kartona'],
  point: ['poen', 'poena', 'poena'],
};

const UNITS: Record<Lang, Record<string, UnitForms>> = {
  de: {
    goal: ['Tor', 'Tore', 'Tore'],
    corner: ['Ecke', 'Ecken', 'Ecken'],
    card: ['Karte', 'Karten', 'Karten'],
    point: ['Punkt', 'Punkte', 'Punkte'],
  },
  sr: SLAVIC_UNITS,
  hr: SLAVIC_UNITS,
  bs: SLAVIC_UNITS,
  it: {
    goal: ['gol', 'gol', 'gol'],
    corner: ['angolo', 'angoli', 'angoli'],
    card: ['cartellino', 'cartellini', 'cartellini'],
    point: ['punto', 'punti', 'punti'],
  },
  fr: {
    goal: ['but', 'buts', 'buts'],
    corner: ['corner', 'corners', 'corners'],
    card: ['carton', 'cartons', 'cartons'],
    point: ['point', 'points', 'points'],
  },
  sq: {
    goal: ['gol', 'gola', 'gola'],
    corner: ['kënd', 'kënde', 'kënde'],
    card: ['karton', 'kartonë', 'kartonë'],
    point: ['pikë', 'pikë', 'pikë'],
  },
  // Turkish keeps the noun singular after a number ("3 gol").
  tr: {
    goal: ['gol', 'gol', 'gol'],
    corner: ['korner', 'korner', 'korner'],
    card: ['kart', 'kart', 'kart'],
    point: ['sayı', 'sayı', 'sayı'],
  },
  es: {
    goal: ['gol', 'goles', 'goles'],
    corner: ['córner', 'córners', 'córners'],
    card: ['tarjeta', 'tarjetas', 'tarjetas'],
    point: ['punto', 'puntos', 'puntos'],
  },
};

const SLAVIC: ReadonlySet<Lang> = new Set(['sr', 'hr', 'bs']);

const unitWord = (lang: Lang, unit: string, count: string) => {
  const forms = UNITS[lang][unit.toLowerCase().replace(/s$/, '')];
  if (!forms) return null;
  const n = Number.parseFloat(count);
  if (n === 1) return forms[0];
  if (
    SLAVIC.has(lang) &&
    Number.isInteger(n) &&
    n % 10 >= 2 &&
    n % 10 <= 4 &&
    (n % 100 < 12 || n % 100 > 14)
  ) {
    return forms[2];
  }
  return forms[1];
};

type SelectionWords = {
  over: string;
  under: string;
  or: string;
  orMore: string;
  yes: string;
  no: string;
  winTo: string;
  love: string;
  noCorner: string;
  corner: string;
  /** Put before a clock time, or around it where "{t}" marks its place. */
  before: string;
  draw: string;
  tie: string;
  /** Tennis "(Svr)": the player serving this game. */
  server: string;
};

const IJEKAVIAN_WORDS: SelectionWords = {
  over: 'Više',
  under: 'Manje',
  or: 'ili',
  orMore: 'ili više',
  yes: 'Da',
  no: 'Ne',
  winTo: 'Osvaja na',
  love: 'nulu',
  noCorner: 'Bez kornera',
  corner: 'Korner',
  before: 'prije',
  draw: 'Neriješeno',
  tie: 'Neriješeno',
  server: 'servis',
};

const SELECTION_WORDS: Record<Lang, SelectionWords> = {
  de: {
    over: 'Über',
    under: 'Unter',
    or: 'oder',
    orMore: 'oder mehr',
    yes: 'Ja',
    no: 'Nein',
    winTo: 'Gewinnt zu',
    love: 'null',
    noCorner: 'Keine Ecke',
    corner: 'Ecke',
    before: 'vor',
    draw: 'Unentschieden',
    tie: 'Unentschieden',
    server: 'Aufschlag',
  },
  sr: {
    over: 'Preko',
    under: 'Ispod',
    or: 'ili',
    orMore: 'ili više',
    yes: 'Da',
    no: 'Ne',
    winTo: 'Osvaja na',
    love: 'nulu',
    noCorner: 'Bez kornera',
    corner: 'Korner',
    before: 'pre',
    draw: 'Nerešeno',
    tie: 'Nerešeno',
    server: 'servis',
  },
  hr: IJEKAVIAN_WORDS,
  bs: IJEKAVIAN_WORDS,
  it: {
    over: 'Over',
    under: 'Under',
    or: 'o',
    orMore: 'o più',
    yes: 'Sì',
    no: 'No',
    winTo: 'Vince a',
    love: 'zero',
    noCorner: 'Nessun angolo',
    corner: 'Angolo',
    before: 'prima delle',
    draw: 'Pareggio',
    tie: 'Pareggio',
    server: 'al servizio',
  },
  fr: {
    over: 'Plus de',
    under: 'Moins de',
    or: 'ou',
    orMore: 'ou plus',
    yes: 'Oui',
    no: 'Non',
    winTo: 'Gagne à',
    love: 'zéro',
    noCorner: 'Pas de corner',
    corner: 'Corner',
    before: 'avant',
    draw: 'Nul',
    tie: 'Égalité',
    server: 'au service',
  },
  sq: {
    over: 'Mbi',
    under: 'Nën',
    or: 'ose',
    orMore: 'ose më shumë',
    yes: 'Po',
    no: 'Jo',
    winTo: 'Fiton me',
    love: 'zero',
    noCorner: 'Pa kënd',
    corner: 'Kënd',
    before: 'para',
    draw: 'Barazim',
    tie: 'Barazim',
    server: 'në shërbim',
  },
  tr: {
    over: 'Üst',
    under: 'Alt',
    or: 'veya',
    orMore: 've üzeri',
    yes: 'Evet',
    no: 'Hayır',
    winTo: 'Kazanır, rakip',
    love: 'sıfır',
    noCorner: 'Korner yok',
    corner: 'Korner',
    // Turkish puts the time first: "Korner (8:00 öncesi)".
    before: '({t} öncesi)',
    draw: 'Beraberlik',
    tie: 'Beraberlik',
    server: 'Servis',
  },
  es: {
    over: 'Más de',
    under: 'Menos de',
    or: 'o',
    orMore: 'o más',
    yes: 'Sí',
    no: 'No',
    winTo: 'Gana a',
    love: 'cero',
    noCorner: 'Sin córner',
    corner: 'Córner',
    before: 'antes de',
    draw: 'Empate',
    tie: 'Empate',
    server: 'al saque',
  },
};

type SelectionTemplates = {
  fullTime: (x: string) => string;
  goalAt: (after: boolean, goal: boolean, t: string) => string;
  scoreBefore: (score: boolean, t: string) => string;
  minutesRange: (a: string, b: string) => string;
  minutes: (over: boolean, n: string) => string;
  exactly: (n: string) => string;
  sets: (n: string) => string;
  period: (n: string) => string;
  nthCard: (n: number) => string;
  gameAndMatch: (n: number, winMatch: boolean) => string;
  /** "Slavia Prague Only" (which teams score). */
  only: (team: string) => string;
  /** "Fenerbahce to score a penalty" / "... to miss a penalty". */
  penalty: (team: string, miss: boolean) => string;
  /** "Card before 29:00" / "No Card before 29:00". */
  cardBefore: (card: boolean, t: string) => string;
  /** "Fenerbahce - Even": a team's goals odd or even. */
  parity: (even: boolean) => string;
  /** "No 4th Goal" on the live next-goal market. */
  noNthGoal: (n: number) => string;
  /** "10th Corner" on the live corners race. */
  nthCorner: (n: number) => string;
  /** A bare "8th" (goalscorer header: scores the 8th goal). */
  ordinal: (n: number) => string;
  breakNext: (team: string) => string;
  /** "X to win 1st set and WIN match" */
  setAndMatch: (team: string, winMatch: boolean) => string;
  /** Basketball margin "BKN Nets by 3+ pts" */
  byPoints: (team: string, n: string) => string;
  setNo: (n: number) => string;
  gameNo: (n: number) => string;
};

const SR_TEMPLATES = (ije: boolean): SelectionTemplates => ({
  fullTime: (x) => `${x} - kraj`,
  goalAt: (after, goal, t) =>
    `${goal ? 'Gol' : 'Bez gola'} ${after ? (ije ? 'poslije' : 'posle') : ije ? 'prije' : 'pre'} ${t}`,
  scoreBefore: (score, t) =>
    `${score ? 'Poentiranje' : 'Bez poentiranja'} ${ije ? 'prije' : 'pre'} ${t}`,
  minutesRange: (a, b) => `Od ${a} do ${b} minuta`,
  minutes: (over, n) => `${over ? 'Više' : 'Manje'} od ${n} minuta`,
  exactly: (n) => `${ije ? 'Tačno' : 'Tačno'} ${n}`,
  sets: (n) => `${n} seta`,
  period: (n) => `${n}. ${ije ? 'trećina' : 'trećina'}`,
  nthCard: (n) => `${n}. karton`,
  gameAndMatch: (n, win) => `Osvaja ${n}. gem i ${win ? 'dobija' : 'gubi'} meč`,
  only: (team) => `Samo ${team}`,
  penalty: (team, miss) =>
    miss ? `${team} promašuje penal` : `${team} daje gol iz penala`,
  cardBefore: (card, t) =>
    `${card ? 'Karton' : 'Bez kartona'} ${ije ? 'prije' : 'pre'} ${t}`,
  parity: (even) => (even ? 'Par' : 'Nepar'),
  noNthGoal: (n) => `Bez ${n}. gola`,
  nthCorner: (n) => `${n}. korner`,
  ordinal: (n) => `${n}.`,
  breakNext: (team) => `${team} pravi ${ije ? 'sljedeći' : 'sledeći'} brejk`,
  setAndMatch: (team, win) =>
    `${team} osvaja 1. set i ${win ? 'dobija' : 'gubi'} meč`,
  byPoints: (team, n) => `${team} sa ${n} poena razlike`,
  setNo: (n) => `${n}. set`,
  gameNo: (n) => `${n}. gem`,
});

const SELECTION_TEMPLATES: Record<Lang, SelectionTemplates> = {
  de: {
    fullTime: (x) => `${x} – Endstand`,
    goalAt: (after, goal, t) =>
      `${goal ? 'Tor' : 'Kein Tor'} ${after ? 'nach' : 'vor'} ${t}`,
    scoreBefore: (score, t) => `${score ? 'Punkte' : 'Keine Punkte'} vor ${t}`,
    minutesRange: (a, b) => `${a} bis ${b} Minuten`,
    minutes: (over, n) => `${over ? 'Über' : 'Unter'} ${n} Minuten`,
    exactly: (n) => `Genau ${n}`,
    sets: (n) => `${n} Sätze`,
    period: (n) => `${n}. Drittel`,
    nthCard: (n) => `${n}. Karte`,
    gameAndMatch: (n, win) =>
      `Gewinnt ${n}. Spiel und ${win ? 'gewinnt' : 'verliert'} das Match`,
    only: (team) => `Nur ${team}`,
    penalty: (team, miss) =>
      `${team} ${miss ? 'verschießt' : 'verwandelt'} einen Elfmeter`,
    cardBefore: (card, t) => `${card ? 'Karte' : 'Keine Karte'} vor ${t}`,
    parity: (even) => (even ? 'Gerade' : 'Ungerade'),
    noNthGoal: (n) => `Kein ${n}. Tor`,
    nthCorner: (n) => `${n}. Ecke`,
    ordinal: (n) => `${n}.`,
    breakNext: (team) => `${team} schafft das nächste Break`,
    setAndMatch: (team, win) =>
      `${team} gewinnt den 1. Satz und ${win ? 'gewinnt' : 'verliert'} das Match`,
    byPoints: (team, n) => `${team} mit ${n} Punkten`,
    setNo: (n) => `${n}. Satz`,
    gameNo: (n) => `${n}. Spiel`,
  },
  sr: SR_TEMPLATES(false),
  hr: {
    ...SR_TEMPLATES(true),
    exactly: (n) => `Točno ${n}`,
    scoreBefore: (score, t) =>
      `${score ? 'Poentiranje' : 'Bez poentiranja'} prije ${t}`,
    gameAndMatch: (n, win) =>
      `Osvaja ${n}. gem i ${win ? 'dobiva' : 'gubi'} meč`,
    penalty: (team, miss) =>
      miss
        ? `${team} promašuje jedanaesterac`
        : `${team} zabija iz jedanaesterca`,
    setAndMatch: (team, win) =>
      `${team} osvaja 1. set i ${win ? 'dobiva' : 'gubi'} meč`,
    byPoints: (team, n) => `${team} s ${n} poena razlike`,
  },
  bs: SR_TEMPLATES(true),
  it: {
    fullTime: (x) => `${x} - finale`,
    goalAt: (after, goal, t) =>
      `${goal ? 'Gol' : 'Nessun gol'} ${after ? 'dopo' : 'prima di'} ${t}`,
    scoreBefore: (score, t) =>
      `${score ? 'Punti' : 'Nessun punto'} prima di ${t}`,
    minutesRange: (a, b) => `Da ${a} a ${b} minuti`,
    minutes: (over, n) => `${over ? 'Più' : 'Meno'} di ${n} minuti`,
    exactly: (n) => `Esattamente ${n}`,
    sets: (n) => `${n} set`,
    period: (n) => `${n}° periodo`,
    nthCard: (n) => `${n}° cartellino`,
    gameAndMatch: (n, win) =>
      `Vince il ${n}° game e ${win ? 'vince' : 'perde'} la partita`,
    only: (team) => `Solo ${team}`,
    penalty: (team, miss) => `${team} ${miss ? 'sbaglia' : 'segna'} un rigore`,
    cardBefore: (card, t) =>
      `${card ? 'Cartellino' : 'Nessun cartellino'} prima di ${t}`,
    parity: (even) => (even ? 'Pari' : 'Dispari'),
    noNthGoal: (n) => `Nessun ${n}° gol`,
    nthCorner: (n) => `${n}° calcio d’angolo`,
    ordinal: (n) => `${n}°`,
    breakNext: (team) => `${team} fa il prossimo break`,
    setAndMatch: (team, win) =>
      `${team} vince il 1° set e ${win ? 'vince' : 'perde'} la partita`,
    byPoints: (team, n) => `${team} con ${n} punti di scarto`,
    setNo: (n) => `${n}° set`,
    gameNo: (n) => `${n}° game`,
  },
  fr: {
    fullTime: (x) => `${x} - fin du match`,
    goalAt: (after, goal, t) =>
      `${goal ? 'But' : 'Pas de but'} ${after ? 'après' : 'avant'} ${t}`,
    scoreBefore: (score, t) => `${score ? 'Points' : 'Aucun point'} avant ${t}`,
    minutesRange: (a, b) => `De ${a} à ${b} minutes`,
    minutes: (over, n) => `${over ? 'Plus' : 'Moins'} de ${n} minutes`,
    exactly: (n) => `Exactement ${n}`,
    sets: (n) => `${n} sets`,
    period: (n) => `${frOrd(n === '1' ? 1 : Number(n), true)} période`,
    nthCard: (n) => `${frOrd(n)} carton`,
    gameAndMatch: (n, win) =>
      `Gagne le ${frOrd(n)} jeu et ${win ? 'gagne' : 'perd'} le match`,
    only: (team) => `Seulement ${team}`,
    penalty: (team, miss) => `${team} ${miss ? 'rate' : 'marque'} un penalty`,
    cardBefore: (card, t) => `${card ? 'Carton' : 'Pas de carton'} avant ${t}`,
    parity: (even) => (even ? 'Pair' : 'Impair'),
    noNthGoal: (n) => `Pas de ${frOrd(n)} but`,
    nthCorner: (n) => `${frOrd(n)} corner`,
    ordinal: (n) => frOrd(n),
    breakNext: (team) => `${team} réussit le prochain break`,
    setAndMatch: (team, win) =>
      `${team} gagne le 1er set et ${win ? 'gagne' : 'perd'} le match`,
    byPoints: (team, n) => `${team} par ${n} points`,
    setNo: (n) => `${frOrd(n)} set`,
    gameNo: (n) => `${frOrd(n)} jeu`,
  },
  sq: {
    fullTime: (x) => `${x} - fundi`,
    goalAt: (after, goal, t) =>
      `${goal ? 'Gol' : 'Pa gol'} ${after ? 'pas' : 'para'} ${t}`,
    scoreBefore: (score, t) => `${score ? 'Pikë' : 'Pa pikë'} para ${t}`,
    minutesRange: (a, b) => `Nga ${a} deri në ${b} minuta`,
    minutes: (over, n) => `${over ? 'Mbi' : 'Nën'} ${n} minuta`,
    exactly: (n) => `Saktësisht ${n}`,
    sets: (n) => `${n} sete`,
    period: (n) => `Periudha ${n}`,
    nthCard: (n) => `Kartoni ${n}`,
    gameAndMatch: (n, win) =>
      `Fiton game ${n} dhe ${win ? 'fiton' : 'humb'} ndeshjen`,
    only: (team) => `Vetëm ${team}`,
    penalty: (team, miss) =>
      `${team} ${miss ? 'humbet' : 'shënon'} një penallti`,
    cardBefore: (card, t) => `${card ? 'Karton' : 'Pa karton'} para ${t}`,
    parity: (even) => (even ? 'Çift' : 'Tek'),
    noNthGoal: (n) => `Pa golin e ${sqOrd(n)}`,
    nthCorner: (n) => `Këndi i ${sqOrd(n)}`,
    ordinal: (n) => `i ${sqOrd(n)}`,
    breakNext: (team) => `${team} thyen shërbimin e radhës`,
    setAndMatch: (team, win) =>
      `${team} fiton setin e parë dhe ${win ? 'fiton' : 'humb'} ndeshjen`,
    byPoints: (team, n) => `${team} me ${n} pikë diferencë`,
    setNo: (n) => `Seti ${n}`,
    gameNo: (n) => `Game ${n}`,
  },
  tr: {
    fullTime: (x) => `${x} - Maç Sonu`,
    goalAt: (after, goal, t) =>
      `${t} ${after ? 'sonrası' : 'öncesi'} ${goal ? 'gol' : 'gol yok'}`,
    scoreBefore: (score, t) => `${t} öncesi ${score ? 'sayı' : 'sayı yok'}`,
    minutesRange: (a, b) => `${a}-${b} dakika`,
    minutes: (over, n) => `${n} dakika ${over ? 'üstü' : 'altı'}`,
    exactly: (n) => `Tam olarak ${n}`,
    sets: (n) => `${n} set`,
    period: (n) => `${n}. Periyot`,
    nthCard: (n) => `${n}. Kart`,
    gameAndMatch: (n, win) =>
      `${n}. oyunu kazanır ve maçı ${win ? 'kazanır' : 'kaybeder'}`,
    only: (team) => `Sadece ${team}`,
    penalty: (team, miss) =>
      `${team} ${miss ? 'penaltı kaçırır' : 'penaltıdan gol atar'}`,
    cardBefore: (card, t) => `${t} öncesi ${card ? 'kart' : 'kart yok'}`,
    parity: (even) => (even ? 'Çift' : 'Tek'),
    noNthGoal: (n) => `${n}. Gol Yok`,
    nthCorner: (n) => `${n}. Korner`,
    ordinal: (n) => `${n}.`,
    breakNext: (team) => `Sıradaki breaki ${team} yapar`,
    setAndMatch: (team, win) =>
      `${team} 1. seti alır ve maçı ${win ? 'kazanır' : 'kaybeder'}`,
    byPoints: (team, n) => `${team} ${n} sayı farkla`,
    setNo: (n) => `${n}. Set`,
    gameNo: (n) => `${n}. Oyun`,
  },
  es: {
    fullTime: (x) => `${x} - final`,
    goalAt: (after, goal, t) =>
      `${goal ? 'Gol' : 'Sin gol'} ${after ? 'después de' : 'antes de'} ${t}`,
    scoreBefore: (score, t) =>
      `${score ? 'Puntos' : 'Sin puntos'} antes de ${t}`,
    minutesRange: (a, b) => `De ${a} a ${b} minutos`,
    minutes: (over, n) => `${over ? 'Más' : 'Menos'} de ${n} minutos`,
    exactly: (n) => `Exactamente ${n}`,
    sets: (n) => `${n} sets`,
    period: (n) => `${n}.º periodo`,
    nthCard: (n) => `${n}.ª tarjeta`,
    gameAndMatch: (n, win) =>
      `Gana el ${n}.º juego y ${win ? 'gana' : 'pierde'} el partido`,
    only: (team) => `Solo ${team}`,
    penalty: (team, miss) => `${team} ${miss ? 'falla' : 'marca'} un penalti`,
    cardBefore: (card, t) =>
      `${card ? 'Tarjeta' : 'Sin tarjeta'} antes de ${t}`,
    parity: (even) => (even ? 'Par' : 'Impar'),
    noNthGoal: (n) => `Sin ${esOrd(n)} gol`,
    nthCorner: (n) => `${esOrd(n)} córner`,
    ordinal: (n) => `${n}.º`,
    breakNext: (team) => `${team} logra el próximo break`,
    setAndMatch: (team, win) =>
      `${team} gana el 1.er set y ${win ? 'gana' : 'pierde'} el partido`,
    byPoints: (team, n) => `${team} por ${n} puntos`,
    setNo: (n) => `${esOrd(n)} set`,
    gameNo: (n) => `${esOrd(n)} juego`,
  },
};

/** Labels with a number or a clock time in them ("Goal after 30:00"). */
const resolveSelectionTemplate = (raw: string, lang: Lang): string | null => {
  const tp = SELECTION_TEMPLATES[lang];
  let m = raw.match(/^(.+) - full time$/i);
  if (m) return tp.fullTime(m[1]);
  m = raw.match(/^(no )?goal (after|before) (\d+:\d+)$/i);
  if (m) return tp.goalAt(/^after$/i.test(m[2]), !m[1], m[3]);
  m = raw.match(/^(no )?score before (\d+:\d+)(?: elapsed)?$/i);
  if (m) return tp.scoreBefore(!m[1], m[2]);
  m = raw.match(/^(\d+) to (\d+) minutes(?: inc\.?)?$/i);
  if (m) return tp.minutesRange(m[1], m[2]);
  m = raw.match(/^(over|under) ([\d.]+) minutes$/i);
  if (m) return tp.minutes(/^over$/i.test(m[1]), m[2]);
  m = raw.match(/^exactly ([\d.]+)$/i);
  if (m) return tp.exactly(m[1]);
  m = raw.match(/^(\d+) sets$/i);
  if (m) return tp.sets(m[1]);
  m = raw.match(/^period (\d+)$/i);
  if (m) return tp.period(m[1]);
  m = raw.match(/^(\d+)(?:st|nd|rd|th) card$/i);
  if (m) return tp.nthCard(Number(m[1]));
  m = raw.match(/^win (\d+)(?:st|nd|rd|th) game & (win|lose) match$/i);
  if (m) return tp.gameAndMatch(Number(m[1]), /^win$/i.test(m[2]));
  m = raw.match(/^(.+) only$/i);
  if (m) return tp.only(m[1]);
  m = raw.match(/^(.+) to (score|miss) a penalty$/i);
  if (m) return tp.penalty(m[1], /^miss$/i.test(m[2]));
  m = raw.match(/^(no )?card before (\d+:\d+)$/i);
  if (m) return tp.cardBefore(!m[1], m[2]);
  m = raw.match(/^(.+) - (even|odd)$/i);
  if (m) return `${m[1]} - ${tp.parity(/^even$/i.test(m[2]))}`;
  m = raw.match(/^no (\d+)(?:st|nd|rd|th) goal$/i);
  if (m) return tp.noNthGoal(Number(m[1]));
  m = raw.match(/^(\d+)(?:st|nd|rd|th) corner$/i);
  if (m) return tp.nthCorner(Number(m[1]));
  m = raw.match(/^(\d+)(?:st|nd|rd|th)$/i);
  if (m) return tp.ordinal(Number(m[1]));
  m = raw.match(/^(.+) to break next$/i);
  if (m) return tp.breakNext(m[1]);
  m = raw.match(/^(.+) to win (?:1st|first) set and (win|lose) match$/i);
  if (m) return tp.setAndMatch(m[1], /^win$/i.test(m[2]));
  m = raw.match(/^(.+) by (\d+(?:-\d+)?\+?) pts?$/i);
  if (m) return tp.byPoints(m[1], m[2]);
  m = raw.match(/^set (\d+)$/i);
  if (m) return tp.setNo(Number(m[1]));
  m = raw.match(/^game (\d+)$/i);
  if (m) return tp.gameNo(Number(m[1]));
  return null;
};

const translateYesNo = (lang: Lang, word: string) =>
  /^yes$/i.test(word) ? SELECTION_WORDS[lang].yes : SELECTION_WORDS[lang].no;

const resolveSelection = (raw: string, lang: Lang): string | null => {
  const hit = DICTIONARIES[lang].selections.get(normalize(raw));
  if (hit) return hit;
  const templated = resolveSelectionTemplate(raw, lang);
  if (templated) return templated;
  // Some selections are market names ("Rushing Yards" on a player prop).
  const asMarket = DICTIONARIES[lang].markets.get(normalize(raw));
  if (asMarket) return asMarket;
  const w = SELECTION_WORDS[lang];

  // "1-3 Goals", "2 Goals", "5+ Goals", "2 or 3 goals", "Over 7 Corners"
  let m = raw.match(/^(\d+)-(\d+) (goals?|corners?|cards?|points?)$/i);
  if (m) {
    const unit = unitWord(lang, m[3], m[2]);
    return unit ? `${m[1]}-${m[2]} ${unit}` : null;
  }
  m = raw.match(/^(\d+)(\+?) (goals?|corners?|cards?|points?)$/i);
  if (m) {
    const unit = unitWord(lang, m[3], m[2] ? '5' : m[1]);
    return unit ? `${m[1]}${m[2]} ${unit}` : null;
  }
  m = raw.match(/^(\d+) or (\d+) (goals?|corners?|cards?|points?)$/i);
  if (m) {
    const unit = unitWord(lang, m[3], m[2]);
    return unit ? `${m[1]} ${w.or} ${m[2]} ${unit}` : null;
  }
  m = raw.match(/^(\d+) or more$/i);
  if (m) return `${m[1]} ${w.orMore}`;
  m = raw.match(
    /^(over|under) ([\d.]+)(?: (goals?|corners?|cards?|points?))?$/i
  );
  if (m) {
    const side = /^over$/i.test(m[1]) ? w.over : w.under;
    if (!m[3]) return `${side} ${m[2]}`;
    const unit = unitWord(lang, m[3], m[2]);
    return unit ? `${side} ${m[2]} ${unit}` : null;
  }
  // "Under 2.5 & No"
  m = raw.match(/^(over|under) ([\d.]+) & (yes|no)$/i);
  if (m) {
    const side = /^over$/i.test(m[1]) ? w.over : w.under;
    return `${side} ${m[2]} & ${translateYesNo(lang, m[3])}`;
  }
  // "To win to 15", "To win to love"
  m = raw.match(/^to win to (love|\d+)$/i);
  if (m) return `${w.winTo} ${/^love$/i.test(m[1]) ? w.love : m[1]}`;
  // Tennis game score "to 15", "to love" (the player is the odd's name).
  m = raw.match(/^to (love|\d+)$/i);
  if (m) return `${w.winTo} ${/^love$/i.test(m[1]) ? w.love : m[1]}`;
  // "Arevalo/Delgado (Svr)": who serves the game.
  m = raw.match(/^(.+) \(svr\)$/i);
  if (m) return `${m[1]} (${w.server})`;
  // Corner handicap "Tie Gimnasia LP": a draw once the line is applied to
  // that team.
  m = raw.match(/^tie (?!-|break\b)(.+)$/i);
  if (m) return `${w.tie} ${m[1]}`;
  // "Draw 1-1" on a period correct score.
  m = raw.match(/^draw (\d+-\d+)$/i);
  if (m) return `${w.draw} ${m[1]}`;
  // Tennis "0 or 15", "0,15 or 30" points the server drops.
  m = raw.match(/^(\d+(?:,\d+)*) or (\d+)$/i);
  if (m) return `${m[1]} ${w.or} ${m[2]}`;
  // Baseball "Aguilas Cibaenas +1.5 & Over 9.5"
  m = raw.match(/^(.+) ([+-][\d.]+) & (over|under) ([\d.]+)$/i);
  if (m) {
    const side = /^over$/i.test(m[3]) ? w.over : w.under;
    return `${m[1]} ${m[2]} & ${side} ${m[4]}`;
  }
  // "Corner before 8:00", "No Corner before 8:00"
  m = raw.match(/^(no )?corner before (\d+:\d+)$/i);
  if (m) {
    const at = w.before.includes('{t}')
      ? w.before.replace('{t}', m[2])
      : `${w.before} ${m[2]}`;
    return `${m[1] ? w.noCorner : w.corner} ${at}`;
  }
  // "Fenerbahce - 2 Goals", "Fenerbahce - 3+ Goals"
  m = raw.match(/^(.+) - (\d+\+? goals?)$/i);
  if (m) {
    const count = resolveSelection(m[2], lang);
    return count ? `${m[1]} - ${count}` : null;
  }
  // HT/FT style "Draw - Draw", "Tie - Tie"; team names pass through.
  m = raw.match(/^(.+) - (.+)$/);
  if (m) {
    const part = (p: string) =>
      /^draw$/i.test(p) ? w.draw : /^tie$/i.test(p) ? w.tie : null;
    const a = part(m[1].trim());
    const b = part(m[2].trim());
    if (a || b) return `${a ?? m[1].trim()} - ${b ?? m[2].trim()}`;
  }
  return null;
};

/**
 * A selection label from the feed ("1st Half Goals", "2-3 Goals", "To win to
 * 30") in the player's language, or null to keep the original.
 */
export function translateSelectionByText(
  raw: string | null | undefined
): string | null {
  // The feed sometimes doubles a space ("Over  13.5").
  const s = String(raw ?? '')
    .trim()
    .replace(/\s+/g, ' ');
  const lang = currentLang();
  if (!s || !lang) return null;
  return resolveSelection(s, lang);
}
